import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarIcon, Check, Clock, User, Mail, Phone, FileText } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingPage = () => {
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    service_type: "",
    appointment_date: null,
    appointment_time: "",
    client_name: "",
    client_email: "",
    client_phone: "",
    notes: ""
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, slotsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/time-slots`)
        ]);
        setServices(servicesRes.data);
        setTimeSlots(slotsRes.data.slots);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // State for blocked times
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [isFullDayBlocked, setIsFullDayBlocked] = useState(false);
  const [blockReasons, setBlockReasons] = useState([]);

  useEffect(() => {
    const fetchAvailability = async () => {
      if (formData.appointment_date) {
        try {
          const dateStr = format(formData.appointment_date, "yyyy-MM-dd");
          
          // Fetch both booked slots and blocked times in parallel
          const [bookedRes, blockedRes] = await Promise.all([
            axios.get(`${API}/appointments/booked-slots/${dateStr}`),
            axios.get(`${API}/blocked-times/date/${dateStr}`)
          ]);
          
          setBookedSlots(bookedRes.data.booked_slots || []);
          setBlockedSlots(blockedRes.data.blocked_slots || []);
          setIsFullDayBlocked(blockedRes.data.is_full_day_blocked || false);
          setBlockReasons(blockedRes.data.reasons || []);
        } catch (error) {
          console.error("Error fetching availability:", error);
        }
      }
    };
    fetchAvailability();
  }, [formData.appointment_date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        appointment_date: format(formData.appointment_date, "yyyy-MM-dd"),
        service_name: selectedService?.name || "",
        estimated_arrival_date: estimatedDelivery ? format(estimatedDelivery, "yyyy-MM-dd") : null
      };
      
      await axios.post(`${API}/appointments`, payload);
      toast.success("Appointment booked successfully!", {
        description: "We'll send you a confirmation email shortly."
      });
      setStep(4); // Success step
    } catch (error) {
      toast.error("Failed to book appointment", {
        description: "Please try again or contact us directly."
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedService = services.find(s => s.id === formData.service_type);

  // Factory closure periods
  const factoryClosures = [
    { start: new Date(2025, 0, 24), end: new Date(2025, 1, 5), label: "Jan 24 - Feb 5" },
    { start: new Date(2025, 3, 4), end: new Date(2025, 3, 7), label: "Apr 4 - Apr 7" }
  ];

  // Check if we should show the factory closure banner (within 1 week of any closure)
  const shouldShowClosureBanner = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (const closure of factoryClosures) {
      const oneWeekBefore = new Date(closure.start);
      oneWeekBefore.setDate(oneWeekBefore.getDate() - 7);
      
      // Show if we're within a week before start OR during the closure period
      if (today >= oneWeekBefore && today <= closure.end) {
        return true;
      }
    }
    return false;
  };

  const showClosureBanner = shouldShowClosureBanner();

  // Check if delivery date falls during or after factory closure
  const getFactoryClosureNotice = (deliveryDate) => {
    if (!deliveryDate) return null;
    
    for (const closure of factoryClosures) {
      // If appointment or delivery falls during closure period, add extra time notice
      if (deliveryDate >= closure.start && deliveryDate <= closure.end) {
        return `Factory closed ${closure.label} - expect longer production time`;
      }
      // If appointment is before closure but delivery would be during/after
      if (formData.appointment_date && new Date(formData.appointment_date) <= closure.end && deliveryDate >= closure.start) {
        return `Note: Factory closed ${closure.label} - production time may be extended`;
      }
    }
    return null;
  };

  // Calculate estimated delivery date based on service
  const getEstimatedDelivery = () => {
    if (!formData.appointment_date || !selectedService) return null;
    
    const appointmentDate = new Date(formData.appointment_date);
    let daysToAdd = 0;
    
    switch (selectedService.id) {
      case "custom-suit":
        daysToAdd = 28; // 4 weeks
        break;
      case "custom-shirt":
        daysToAdd = 28; // 4 weeks
        break;
      case "wedding-attire":
        daysToAdd = 28; // 4 weeks
        break;
      case "alteration":
        daysToAdd = 10; // 10 days
        break;
      case "consultation":
        return null; // No delivery for consultation
      default:
        daysToAdd = 28;
    }
    
    const deliveryDate = new Date(appointmentDate);
    deliveryDate.setDate(deliveryDate.getDate() + daysToAdd);
    return deliveryDate;
  };

  const estimatedDelivery = getEstimatedDelivery();
  const factoryNotice = getFactoryClosureNotice(estimatedDelivery);

  const canProceedStep1 = formData.service_type !== "";
  const canProceedStep2 = formData.appointment_date && formData.appointment_time !== "";
  const canProceedStep3 = formData.client_name && formData.client_email && formData.client_phone;

  const steps = [
    { number: 1, label: "Service" },
    { number: 2, label: "Date & Time" },
    { number: 3, label: "Details" }
  ];

  return (
    <div data-testid="booking-page" className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-16 md:py-20 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="mono-text text-primary mb-6">Schedule Your Visit</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-6" data-testid="booking-page-title">
              Book an Appointment
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto font-light mb-4">
              Begin your custom tailoring journey with a private consultation at laguerre.co
            </p>
            <p className="text-sm text-foreground/60 max-w-md mx-auto">
              Consultations are complimentary and last approximately 45 minutes.
            </p>
            {showClosureBanner && (
              <div className="mt-6 max-w-xl mx-auto">
                <p className="text-xs text-yellow-500/80 bg-yellow-500/10 border border-yellow-500/20 px-4 py-2 rounded">
                  ⚠️ Factory closed: Jan 24 - Feb 5 & Apr 4 - Apr 7. Orders during these periods may have extended production times.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Progress Steps */}
      {step < 4 && (
        <section className="py-8 border-b border-white/5">
          <div className="max-w-[800px] mx-auto px-6 md:px-12">
            <div className="flex items-center justify-center gap-4 md:gap-8">
              {steps.map((s, index) => (
                <div key={s.number} className="flex items-center">
                  <div className="flex items-center gap-2">
                    <div
                      data-testid={`step-indicator-${s.number}`}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${
                        step > s.number
                          ? "bg-green-600 text-white"
                          : step === s.number
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {step > s.number ? <Check size={14} /> : s.number}
                    </div>
                    <span className={`hidden md:block text-sm ${
                      step >= s.number ? "text-foreground" : "text-muted-foreground"
                    }`}>
                      {s.label}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-12 md:w-24 h-px mx-4 ${
                      step > s.number ? "bg-green-600" : "bg-muted"
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Form Steps */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12">
          {/* Step 1: Service Selection */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-serif text-2xl mb-8 text-center" data-testid="step1-title">
                Select a Service
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {services.map((service) => (
                  <button
                    key={service.id}
                    data-testid={`service-option-${service.id}`}
                    onClick={() => setFormData({ ...formData, service_type: service.id })}
                    className={`text-left p-6 border transition-all duration-300 ${
                      formData.service_type === service.id
                        ? "border-primary bg-primary/5"
                        : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <h3 className="font-serif text-lg mb-2">{service.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {service.description}
                    </p>
                    <div className="flex justify-between text-sm">
                      <span className="text-primary">{service.duration}</span>
                      <span>{service.price}</span>
                    </div>
                  </button>
                ))}
              </div>
              <div className="mt-12 flex justify-center">
                <Button
                  data-testid="step1-next-btn"
                  onClick={() => setStep(2)}
                  disabled={!canProceedStep1}
                  className="bg-primary text-primary-foreground px-12 py-6 text-xs uppercase tracking-[0.2em] rounded-none hover:bg-foreground"
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Date & Time */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-serif text-2xl mb-8 text-center" data-testid="step2-title">
                Choose Date & Time
              </h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Calendar */}
                <div className="flex justify-center">
                  <div className="bg-card border border-white/10 p-6">
                    <Calendar
                      data-testid="booking-calendar"
                      mode="single"
                      selected={formData.appointment_date}
                      onSelect={(date) => setFormData({ ...formData, appointment_date: date, appointment_time: "" })}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today || date.getDay() === 0;
                      }}
                      className="rounded-none"
                    />
                  </div>
                </div>

                {/* Time Slots */}
                <div>
                  <p className="mono-text text-primary mb-4 text-center lg:text-left">Available Times</p>
                  {formData.appointment_date ? (
                    isFullDayBlocked ? (
                      <div className="bg-red-500/10 border border-red-500/30 p-6 rounded-lg text-center">
                        <p className="text-red-400 font-medium mb-2">This day is not available</p>
                        {blockReasons.length > 0 && (
                          <p className="text-sm text-muted-foreground">{blockReasons.join(", ")}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">Please select a different date</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        {timeSlots.map((slot) => {
                          const isBooked = bookedSlots.includes(slot);
                          const isBlocked = blockedSlots.includes(slot);
                          const isUnavailable = isBooked || isBlocked;
                          return (
                            <button
                              key={slot}
                              data-testid={`time-slot-${slot.replace(/[: ]/g, "-").toLowerCase()}`}
                              onClick={() => !isUnavailable && setFormData({ ...formData, appointment_time: slot })}
                              disabled={isUnavailable}
                              className={`p-4 border text-sm transition-all duration-300 ${
                                isUnavailable
                                  ? "border-white/5 text-muted-foreground/50 cursor-not-allowed"
                                  : formData.appointment_time === slot
                                  ? "border-primary bg-primary/10 text-primary"
                                  : "border-white/10 hover:border-primary"
                              }`}
                            >
                              <Clock size={14} strokeWidth={1.5} className="inline mr-2" />
                              {slot}
                              {isBooked && <span className="block text-xs mt-1">Booked</span>}
                              {isBlocked && !isBooked && <span className="block text-xs mt-1 text-red-400">Blocked</span>}
                            </button>
                          );
                        })}
                      </div>
                    )
                  ) : (
                    <p className="text-muted-foreground text-center lg:text-left">
                      Please select a date first
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-12 flex justify-center gap-4">
                <Button
                  data-testid="step2-back-btn"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="px-8 py-6 text-xs uppercase tracking-[0.2em] rounded-none border-white/20"
                >
                  Back
                </Button>
                <Button
                  data-testid="step2-next-btn"
                  onClick={() => setStep(3)}
                  disabled={!canProceedStep2}
                  className="bg-primary text-primary-foreground px-12 py-6 text-xs uppercase tracking-[0.2em] rounded-none hover:bg-foreground"
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Personal Details */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-serif text-2xl mb-8 text-center" data-testid="step3-title">
                Your Details
              </h2>

              <form onSubmit={handleSubmit} className="max-w-lg mx-auto space-y-6">
                {/* Summary */}
                <div className="bg-card border border-white/10 p-6 mb-8">
                  <p className="mono-text text-primary text-[10px] mb-4">Appointment Summary</p>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-muted-foreground">Service:</span> {selectedService?.name}</p>
                    <p><span className="text-muted-foreground">Date:</span> {formData.appointment_date && format(formData.appointment_date, "MMMM d, yyyy")}</p>
                    <p><span className="text-muted-foreground">Time:</span> {formData.appointment_time}</p>
                    <p><span className="text-muted-foreground">Duration:</span> {selectedService?.duration}</p>
                    {estimatedDelivery && selectedService?.id !== "consultation" && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <p className="text-primary font-medium">
                          Estimated Delivery: {format(estimatedDelivery, "MMMM d, yyyy")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Your {selectedService?.name.toLowerCase()} should be ready by this date
                        </p>
                        {factoryNotice && (
                          <p className="text-xs text-yellow-500 mt-2 flex items-center gap-1">
                            ⚠️ {factoryNotice}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="relative">
                  <User size={18} strokeWidth={1.5} className="absolute left-0 top-4 text-muted-foreground" />
                  <Input
                    data-testid="input-name"
                    placeholder="Full Name"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    className="luxury-input pl-8"
                    required
                  />
                </div>

                <div className="relative">
                  <Mail size={18} strokeWidth={1.5} className="absolute left-0 top-4 text-muted-foreground" />
                  <Input
                    data-testid="input-email"
                    type="email"
                    placeholder="Email Address"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    className="luxury-input pl-8"
                    required
                  />
                </div>

                <div className="relative">
                  <Phone size={18} strokeWidth={1.5} className="absolute left-0 top-4 text-muted-foreground" />
                  <Input
                    data-testid="input-phone"
                    type="tel"
                    placeholder="Phone Number"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    className="luxury-input pl-8"
                    required
                  />
                </div>

                <div className="relative">
                  <FileText size={18} strokeWidth={1.5} className="absolute left-0 top-4 text-muted-foreground" />
                  <Textarea
                    data-testid="input-notes"
                    placeholder="Additional notes or requests (optional)"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="luxury-input pl-8 min-h-[100px] resize-none"
                  />
                </div>

                <div className="pt-6 flex flex-col items-center gap-4">
                  <div className="flex justify-center gap-4">
                    <Button
                      type="button"
                      data-testid="step3-back-btn"
                      variant="outline"
                      onClick={() => setStep(2)}
                      className="px-8 py-6 text-xs uppercase tracking-[0.2em] rounded-none border-white/20"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      data-testid="step3-submit-btn"
                      disabled={!canProceedStep3 || loading}
                      className="bg-primary text-primary-foreground px-12 py-6 text-xs uppercase tracking-[0.2em] rounded-none hover:bg-foreground"
                    >
                      {loading ? "Booking..." : "Confirm Booking"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    No obligation · Personal styling guidance included
                  </p>
                </div>
              </form>
            </motion.div>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <div className="w-20 h-20 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-8">
                <Check size={40} className="text-green-500" />
              </div>
              <h2 className="font-serif text-3xl mb-4" data-testid="booking-success-title">
                Appointment Confirmed
              </h2>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                Thank you for booking with laguerre.co. We&apos;ve sent a confirmation 
                email with all the details. We look forward to seeing you.
              </p>
              <div className="bg-card border border-white/10 p-8 max-w-md mx-auto mb-8">
                <div className="space-y-3 text-sm text-left">
                  <p><span className="text-muted-foreground">Service:</span> {selectedService?.name}</p>
                  <p><span className="text-muted-foreground">Date:</span> {formData.appointment_date && format(formData.appointment_date, "MMMM d, yyyy")}</p>
                  <p><span className="text-muted-foreground">Time:</span> {formData.appointment_time}</p>
                  <p><span className="text-muted-foreground">Name:</span> {formData.client_name}</p>
                  {estimatedDelivery && selectedService?.id !== "consultation" && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-primary font-medium">
                        Estimated Arrival: {format(estimatedDelivery, "MMMM d, yyyy")}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Your {selectedService?.name.toLowerCase()} should be ready by this date
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  data-testid="booking-done-btn"
                  onClick={() => window.location.href = "/"}
                  className="bg-primary text-primary-foreground px-12 py-6 text-xs uppercase tracking-[0.2em] rounded-none hover:bg-foreground"
                >
                  Return Home
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.location.href = "/manage-appointment"}
                  className="px-8 py-6 text-xs uppercase tracking-[0.2em] rounded-none border-white/20"
                >
                  Manage Appointment
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
